import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';

const API_URL = (import.meta as any).env?.VITE_API_URL || 'https://fitpulse.es/api';
const SUPERADMIN = 'ahmedessabbar12@hotmail.com';

interface Coupon {
  id: string; code: string; discount: number; maxUses: number;
  usedCount: number; expiresAt: string | null; isActive: boolean;
  description: string | null; createdAt: string;
}
interface User {
  id: string; email: string; firstName: string; lastName: string;
  role: string; createdAt: string;
  subscription?: { plan: string; status: string };
}
interface Business {
  id: string; name: string; city: string; slug: string;
  owner?: { email: string };
  subscription?: { plan: string; status: string };
}

export function SuperAdmin() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<'dashboard'|'cupones'|'usuarios'|'negocios'|'suscripciones'>('dashboard');
  const [loading, setLoading] = useState(true);
  const [authorized, setAuthorized] = useState(false);
  const [stats, setStats] = useState<any>(null);
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [showCouponForm, setShowCouponForm] = useState(false);
  const [couponForm, setCouponForm] = useState({ code: '', discount: 20, maxUses: 10, expiresAt: '', description: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    if (!token) { navigate('/login'); return; }
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      if (payload.email !== SUPERADMIN) { navigate('/dashboard'); return; }
      setAuthorized(true);
      loadAll(token);
    } catch { navigate('/login'); }
  }, []);

  const token = () => localStorage.getItem('accessToken');
  const headers = () => ({ Authorization: `Bearer ${token()}` });

  const loadAll = async (t: string) => {
    setLoading(true);
    try {
      const h = { Authorization: `Bearer ${t}` };
      const [statsR, couponsR] = await Promise.all([
        axios.get(`${API_URL}/stats`, { headers: h }).catch(() => null),
        axios.get(`${API_URL}/coupons`, { headers: h }).catch(() => ({ data: { data: { coupons: [] } } })),
      ]);
      if (statsR) setStats(statsR.data.data);
      setCoupons(couponsR.data.data.coupons || []);
    } catch {}
    finally { setLoading(false); }
  };

  const loadUsers = async () => {
    try {
      const r = await axios.get(`${API_URL}/admin/users`, { headers: headers() });
      setUsers(r.data.data.users || []);
    } catch { toast.error('Error cargando usuarios'); }
  };

  const loadBusinesses = async () => {
    try {
      const r = await axios.get(`${API_URL}/admin/businesses`, { headers: headers() });
      setBusinesses(r.data.data.businesses || []);
    } catch { toast.error('Error cargando negocios'); }
  };

  const createCoupon = async () => {
    if (!couponForm.code || !couponForm.discount) { toast.error('Rellena código y descuento'); return; }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/coupons`, couponForm, { headers: headers() });
      toast.success('✅ Cupón creado');
      setShowCouponForm(false);
      setCouponForm({ code: '', discount: 20, maxUses: 10, expiresAt: '', description: '' });
      const r = await axios.get(`${API_URL}/coupons`, { headers: headers() });
      setCoupons(r.data.data.coupons || []);
    } catch (e: any) { toast.error(e?.response?.data?.message || 'Error'); }
    finally { setSaving(false); }
  };

  const toggleCoupon = async (id: string, isActive: boolean) => {
    await axios.patch(`${API_URL}/coupons/${id}`, { isActive: !isActive }, { headers: headers() });
    const r = await axios.get(`${API_URL}/coupons`, { headers: headers() });
    setCoupons(r.data.data.coupons || []);
    toast.success(isActive ? 'Cupón desactivado' : 'Cupón activado');
  };

  const deleteCoupon = async (id: string) => {
    if (!confirm('¿Eliminar cupón?')) return;
    await axios.delete(`${API_URL}/coupons/${id}`, { headers: headers() });
    setCoupons(c => c.filter(x => x.id !== id));
    toast.success('Cupón eliminado');
  };

  const activateSub = async (userId: string, plan: string) => {
    try {
      await axios.post(`${API_URL}/admin/activate-subscription`, { userId, plan }, { headers: headers() });
      toast.success(`✅ Plan ${plan} activado`);
      loadUsers();
    } catch { toast.error('Error activando suscripción'); }
  };

  const generateCode = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    setCouponForm(f => ({ ...f, code: 'FIT' + Array.from({length:6},()=>chars[Math.floor(Math.random()*chars.length)]).join('') }));
  };

  if (!authorized || loading) return (
    <div className="flex items-center justify-center h-screen bg-gray-900">
      <div className="text-white text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
        <p>Verificando acceso...</p>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: '#0f172a', fontFamily: 'Inter, sans-serif' }}>
      <style>{`
        .sa-nav { background: #1e293b; border-bottom: 1px solid #334155; padding: 0 24px; height: 60px; display: flex; align-items: center; justify-content: space-between; position: sticky; top: 0; z-index: 50; }
        .sa-tab { padding: 8px 16px; border-radius: 8px; border: none; background: none; color: #64748b; font-weight: 600; font-size: .82rem; cursor: pointer; font-family: Inter, sans-serif; transition: all .2s; }
        .sa-tab.active { background: #3b82f6; color: white; }
        .sa-card { background: #1e293b; border: 1px solid #334155; border-radius: 14px; padding: 20px; }
        .sa-input { width: 100%; padding: 10px 14px; background: #0f172a; border: 1px solid #334155; border-radius: 8px; color: white; font-family: Inter, sans-serif; font-size: .88rem; outline: none; }
        .sa-input:focus { border-color: #3b82f6; }
        .sa-label { font-size: .75rem; font-weight: 600; color: #64748b; margin-bottom: 6px; display: block; text-transform: uppercase; letter-spacing: .06em; }
        .sa-btn { padding: 9px 18px; border-radius: 8px; border: none; font-weight: 700; font-size: .82rem; cursor: pointer; font-family: Inter, sans-serif; transition: all .2s; }
        .sa-btn-blue { background: #3b82f6; color: white; }
        .sa-btn-blue:hover { background: #2563eb; }
        .sa-btn-red { background: #dc2626; color: white; }
        .sa-btn-gray { background: #334155; color: #94a3b8; }
        .sa-table { width: 100%; border-collapse: collapse; }
        .sa-table th { padding: 10px 14px; text-align: left; font-size: .7rem; font-weight: 700; color: #475569; text-transform: uppercase; letter-spacing: .08em; border-bottom: 1px solid #334155; }
        .sa-table td { padding: 12px 14px; font-size: .82rem; color: #e2e8f0; border-bottom: 1px solid #1e293b; }
        .sa-table tr:hover td { background: rgba(255,255,255,.02); }
        .toggle { position: relative; width: 40px; height: 22px; background: #334155; border-radius: 100px; cursor: pointer; transition: background .3s; border: none; }
        .toggle.on { background: #10b981; }
        .toggle-knob { position: absolute; top: 2px; left: 2px; width: 18px; height: 18px; background: white; border-radius: 50%; transition: transform .3s; }
        .toggle.on .toggle-knob { transform: translateX(18px); }
        .plan-badge { font-size: .65rem; font-weight: 700; padding: 3px 8px; border-radius: 100px; }
      `}</style>

      {/* NAV */}
      <div className="sa-nav">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ fontWeight: 900, fontSize: '1rem', color: '#3b82f6' }}>● FitPulse</div>
          <span style={{ background: '#dc2626', color: 'white', fontSize: '.65rem', fontWeight: 700, padding: '2px 8px', borderRadius: 100 }}>SUPERADMIN</span>
        </div>
        <div style={{ display: 'flex', gap: 4 }}>
          {(['dashboard','cupones','usuarios','negocios','suscripciones'] as const).map(t => (
            <button key={t} className={`sa-tab ${tab===t?'active':''}`} onClick={() => {
              setTab(t);
              if (t==='usuarios') loadUsers();
              if (t==='negocios') loadBusinesses();
              if (t==='suscripciones') loadUsers();
            }}>
              {t.charAt(0).toUpperCase()+t.slice(1)}
            </button>
          ))}
        </div>
        <button onClick={() => navigate('/dashboard')} className="sa-btn sa-btn-gray">← Volver al panel</button>
      </div>

      <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }}>

        {/* DASHBOARD */}
        {tab === 'dashboard' && (
          <div>
            <h1 style={{ color: 'white', fontWeight: 800, fontSize: '1.4rem', marginBottom: 24 }}>Panel de control</h1>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16, marginBottom: 24 }}>
              {[
                { label: 'Cupones activos', value: coupons.filter(c=>c.isActive).length, color: '#3b82f6', icon: '🎟️' },
                { label: 'Total usos cupones', value: coupons.reduce((s,c)=>s+c.usedCount,0), color: '#10b981', icon: '✅' },
                { label: 'Reservas hoy', value: stats?.reservasHoy || 0, color: '#f59e0b', icon: '📅' },
                { label: 'Ingresos mes', value: `€${stats?.ingresosMes || 0}`, color: '#6366f1', icon: '💰' },
              ].map((s,i) => (
                <div key={i} className="sa-card">
                  <div style={{ fontSize: '1.5rem', marginBottom: 8 }}>{s.icon}</div>
                  <div style={{ fontSize: '1.8rem', fontWeight: 800, color: s.color }}>{s.value}</div>
                  <div style={{ fontSize: '.72rem', color: '#64748b', marginTop: 4 }}>{s.label}</div>
                </div>
              ))}
            </div>
            <div className="sa-card">
              <p style={{ color: '#64748b', fontSize: '.85rem' }}>
                Bienvenido al panel de superadmin de FitPulse. Desde aquí puedes gestionar cupones, usuarios, negocios y suscripciones.
              </p>
            </div>
          </div>
        )}

        {/* CUPONES */}
        {tab === 'cupones' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
              <h2 style={{ color: 'white', fontWeight: 800, fontSize: '1.2rem' }}>🎟️ Cupones de descuento</h2>
              <button className="sa-btn sa-btn-blue" onClick={() => setShowCouponForm(!showCouponForm)}>
                {showCouponForm ? '✕ Cancelar' : '+ Nuevo cupón'}
              </button>
            </div>

            {showCouponForm && (
              <div className="sa-card" style={{ marginBottom: 20 }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 14 }}>
                  <div>
                    <label className="sa-label">Código</label>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <input className="sa-input" value={couponForm.code} onChange={e => setCouponForm(f=>({...f,code:e.target.value.toUpperCase()}))} placeholder="FITPULSE20"/>
                      <button className="sa-btn sa-btn-gray" onClick={generateCode}>🎲</button>
                    </div>
                  </div>
                  <div>
                    <label className="sa-label">Descuento %</label>
                    <input className="sa-input" type="number" min={1} max={100} value={couponForm.discount} onChange={e => setCouponForm(f=>({...f,discount:parseInt(e.target.value)}))}/>
                  </div>
                  <div>
                    <label className="sa-label">Usos máximos</label>
                    <input className="sa-input" type="number" min={1} value={couponForm.maxUses} onChange={e => setCouponForm(f=>({...f,maxUses:parseInt(e.target.value)}))}/>
                  </div>
                  <div>
                    <label className="sa-label">Expira</label>
                    <input className="sa-input" type="date" value={couponForm.expiresAt} onChange={e => setCouponForm(f=>({...f,expiresAt:e.target.value}))}/>
                  </div>
                  <div style={{ gridColumn: 'span 2' }}>
                    <label className="sa-label">Descripción</label>
                    <input className="sa-input" value={couponForm.description} onChange={e => setCouponForm(f=>({...f,description:e.target.value}))} placeholder="Descuento de bienvenida"/>
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
                  <button className="sa-btn sa-btn-blue" onClick={createCoupon} disabled={saving}>{saving?'Creando...':'✅ Crear cupón'}</button>
                </div>
              </div>
            )}

            <div className="sa-card">
              <table className="sa-table">
                <thead>
                  <tr>
                    <th>Código</th><th>Descuento</th><th>Usos</th><th>Expira</th><th>Descripción</th><th>Estado</th><th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {coupons.map(c => (
                    <tr key={c.id}>
                      <td><span style={{ fontFamily: 'monospace', fontWeight: 700, color: '#60a5fa' }}>{c.code}</span></td>
                      <td><span style={{ color: '#34d399', fontWeight: 700 }}>{c.discount}%</span></td>
                      <td>{c.usedCount}/{c.maxUses}</td>
                      <td style={{ color: c.expiresAt && new Date(c.expiresAt) < new Date() ? '#ef4444' : '#94a3b8' }}>
                        {c.expiresAt ? new Date(c.expiresAt).toLocaleDateString('es-ES') : '—'}
                      </td>
                      <td style={{ color: '#64748b' }}>{c.description || '—'}</td>
                      <td>
                        <button className={`toggle ${c.isActive?'on':''}`} onClick={() => toggleCoupon(c.id, c.isActive)}>
                          <div className="toggle-knob"></div>
                        </button>
                      </td>
                      <td>
                        <button className="sa-btn sa-btn-red" style={{ padding: '5px 10px', fontSize: '.72rem' }} onClick={() => deleteCoupon(c.id)}>🗑️</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {coupons.length === 0 && <p style={{ textAlign: 'center', color: '#475569', padding: 32 }}>Sin cupones todavía</p>}
            </div>
          </div>
        )}

        {/* USUARIOS */}
        {tab === 'usuarios' && (
          <div>
            <h2 style={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', marginBottom: 24 }}>👥 Usuarios</h2>
            <div className="sa-card">
              <table className="sa-table">
                <thead>
                  <tr><th>Email</th><th>Nombre</th><th>Rol</th><th>Plan</th><th>Registro</th></tr>
                </thead>
                <tbody>
                  {users.map(u => (
                    <tr key={u.id}>
                      <td style={{ color: '#60a5fa' }}>{u.email}</td>
                      <td>{u.firstName} {u.lastName}</td>
                      <td><span style={{ background: u.role==='OWNER'?'#dbeafe':'#dcfce7', color: u.role==='OWNER'?'#1d4ed8':'#15803d', fontSize: '.68rem', fontWeight: 700, padding: '2px 8px', borderRadius: 100 }}>{u.role}</span></td>
                      <td>
                        {u.subscription ? (
                          <span className="plan-badge" style={{ background: u.subscription.status==='ACTIVE'?'#dcfce7':'#fee2e2', color: u.subscription.status==='ACTIVE'?'#15803d':'#dc2626' }}>
                            {u.subscription.plan} · {u.subscription.status}
                          </span>
                        ) : <span style={{ color: '#475569', fontSize: '.75rem' }}>Sin plan</span>}
                      </td>
                      <td style={{ color: '#475569' }}>{new Date(u.createdAt).toLocaleDateString('es-ES')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {users.length === 0 && <p style={{ textAlign: 'center', color: '#475569', padding: 32 }}>Cargando usuarios...</p>}
            </div>
          </div>
        )}

        {/* NEGOCIOS */}
        {tab === 'negocios' && (
          <div>
            <h2 style={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', marginBottom: 24 }}>🏢 Negocios</h2>
            <div className="sa-card">
              <table className="sa-table">
                <thead>
                  <tr><th>Nombre</th><th>Ciudad</th><th>Propietario</th><th>Plan</th><th>Acciones</th></tr>
                </thead>
                <tbody>
                  {businesses.map(b => (
                    <tr key={b.id}>
                      <td style={{ fontWeight: 600 }}>{b.name}</td>
                      <td style={{ color: '#64748b' }}>{b.city || '—'}</td>
                      <td style={{ color: '#60a5fa' }}>{b.owner?.email || '—'}</td>
                      <td>
                        {b.subscription ? (
                          <span className="plan-badge" style={{ background: b.subscription.status==='ACTIVE'?'#dcfce7':'#fee2e2', color: b.subscription.status==='ACTIVE'?'#15803d':'#dc2626' }}>
                            {b.subscription.plan}
                          </span>
                        ) : '—'}
                      </td>
                      <td>
                        <a href={`https://admin.fitpulse.es/b/${b.slug}`} target="_blank" rel="noreferrer" className="sa-btn sa-btn-gray" style={{ fontSize: '.72rem', padding: '5px 10px', textDecoration: 'none', display: 'inline-block' }}>
                          Ver →
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {businesses.length === 0 && <p style={{ textAlign: 'center', color: '#475569', padding: 32 }}>Cargando negocios...</p>}
            </div>
          </div>
        )}

        {/* SUSCRIPCIONES */}
        {tab === 'suscripciones' && (
          <div>
            <h2 style={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', marginBottom: 24 }}>💳 Gestión de suscripciones</h2>
            <div className="sa-card">
              <table className="sa-table">
                <thead>
                  <tr><th>Email</th><th>Plan actual</th><th>Estado</th><th>Activar plan</th></tr>
                </thead>
                <tbody>
                  {users.filter(u => u.role === 'OWNER').map(u => (
                    <tr key={u.id}>
                      <td style={{ color: '#60a5fa' }}>{u.email}</td>
                      <td>
                        <span className="plan-badge" style={{ background: '#1e293b', color: '#94a3b8' }}>
                          {u.subscription?.plan || 'FREE'}
                        </span>
                      </td>
                      <td>
                        <span className="plan-badge" style={{ background: u.subscription?.status==='ACTIVE'?'#dcfce7':'#fee2e2', color: u.subscription?.status==='ACTIVE'?'#15803d':'#dc2626' }}>
                          {u.subscription?.status || 'SIN PLAN'}
                        </span>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 6 }}>
                          {['STARTER','PRO','BUSINESS'].map(plan => (
                            <button key={plan} onClick={() => activateSub(u.id, plan)}
                              className="sa-btn sa-btn-blue" style={{ padding: '4px 10px', fontSize: '.68rem' }}>
                              {plan}
                            </button>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
